import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ArlInformationInputComponent } from '../../../../arl-information-input/arl-information-input.component';
import { DaveDoubleIconModule } from '../../../../../../dave-double-icon/dave-double-icon.module';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import {
    AccountsReceivableLedgerEntity,
    AccountsReceivableLedgerTypeEnum,
} from '../../../../../../dave-data-module/entities/accounts-receivable-ledger.entity';
import { ResourcePageMeta } from '../../../../../../helper/page-metadata';
import { arlTableData, determineIfIsArlTableDataOrGroupEnd, groupEndPlaceholder } from '../../arl-table.component';
import { BehaviorSubject, merge, of, Subscription, switchMap } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { compareEqualityAsJsonString } from '../../../../../../helper/helper';
import { MaterialDataService } from '../../../../../../dave-data-module/services/material-data.service';
import { Store } from '@ngrx/store';
import { State } from '../../../../../../dave-data-module/State';
import { getResourceById } from '../../../../../../dave-data-module/State/selectors/resource-dispo/resource.selectors';
import { getJobSpecificationById } from '../../../../../../dave-data-module/State/selectors/job-specification.selector';

@Component({
    selector: 'app-arl-table-cell-information',
    standalone: true,
    imports: [CommonModule, FontAwesomeModule, ArlInformationInputComponent, DaveDoubleIconModule, MatLegacyButtonModule, MatLegacyTooltipModule],
    templateUrl: './arl-table-cell-information.component.html',
    styleUrls: ['./arl-table-cell-information.component.scss']
})
export class ArlTableCellInformationComponent implements OnDestroy {
    materialData = inject(MaterialDataService);
    store = inject(Store<State>);

    protected groupRowData: groupEndPlaceholder;
    protected type: AccountsReceivableLedgerTypeEnum;
    protected positionRowData: arlTableData<AccountsReceivableLedgerEntity>;

    private formValues$ = new BehaviorSubject<{materialId: number | null, resourceId: number | null, jobSpecificationId: number | null} | null>(null);
    private formValueSubscriptin: Subscription;

    protected material$ = this.formValues$.pipe(
        map((v) => v?.materialId),
        distinctUntilChanged(),
        switchMap((id) => id ? this.materialData.getMaterialById$(id) : of(null))
    );
    protected resource$ = this.formValues$.pipe(
        map((v) => v?.resourceId),
        distinctUntilChanged(),
        switchMap((id) => id ? this.store.select(getResourceById({id})) : of(null))
    );
    protected jobSpecification$ = this.formValues$.pipe(
        map((v) => v?.jobSpecificationId),
        distinctUntilChanged(),
        switchMap((id) => id ? this.store.select(getJobSpecificationById({id})) : of(null))
    );

    @Input() set rowData(v: arlTableData<AccountsReceivableLedgerEntity> | groupEndPlaceholder) {
        this.formValueSubscriptin?.unsubscribe();
        if (determineIfIsArlTableDataOrGroupEnd(v)) {
            this.groupRowData = null;
            this.positionRowData = v;
            // todo auf observable umbauen falls der typ irgendwann änderbar ist
            this.type = v.form.controls.Type.getRawValue();
            this.setFormValueSubscription()
        } else {
            this.groupRowData = v;
            this.positionRowData = null;
            this.type = null;
        }
        this.setFormValue();
    }
    @Input() _disabled: boolean;

    @Output() setEntityId = new EventEmitter<{
        id: number | null,
        m2s?: {
            supplierId: number;
            listPrice: number;
            cost: number;
            sellPrice: number;
            articleNumber?: string;
        },
    }>();

    protected readonly ARLTypeEnum = AccountsReceivableLedgerTypeEnum;
    protected readonly ResourcePageMeta = ResourcePageMeta;

    private setFormValueSubscription() {
        const formControls = this.positionRowData.form.controls;
        this.formValueSubscriptin = merge(formControls.MaterialId.valueChanges, formControls.ResourceId.valueChanges, formControls.JobSpecificationId.valueChanges).subscribe(() => {
            this.setFormValue();
        });
    }
    private setFormValue() {
        if (!this.positionRowData) {
            this.formValues$.next(null)
        } else {
            const formControls = this.positionRowData.form.controls;
            const newValue = {
                materialId: formControls.MaterialId.getRawValue(),
                resourceId: formControls.ResourceId.getRawValue(),
                jobSpecificationId: formControls.JobSpecificationId.getRawValue(),
            }
            if (!compareEqualityAsJsonString(newValue, this.formValues$.value)) {
                this.formValues$.next(newValue)
            }
        }
    }
    protected getEntityId() {
        return this.type === AccountsReceivableLedgerTypeEnum.ResourceArl
            ? this.positionRowData.form.controls.ResourceId.value
            : this.type === AccountsReceivableLedgerTypeEnum.MaterialArl
                ? this.positionRowData.form.controls.MaterialId.value
                : this.type === AccountsReceivableLedgerTypeEnum.EmployeeArl
                    ? this.positionRowData.form.controls.JobSpecificationId.value
                    : null
    }
    protected getInputMode(): ArlInformationInputComponent['inputMode'] {
        return this.type === AccountsReceivableLedgerTypeEnum.ResourceArl || this.type === AccountsReceivableLedgerTypeEnum.MaterialArl || this.type === AccountsReceivableLedgerTypeEnum.EmployeeArl
            ? 'pseudoSelectSearch'//'selectSearch'
            : 'autocomplete'

    }
    ngOnDestroy(): void {
        this.formValueSubscriptin?.unsubscribe();
    }
}
